/**
 * The design code of Parta: one system for every role. Big, clean white cards set close together
 * on a grey page, so every card stands out; flat colours only (no gradients); one accent, lavender
 * violet, with a soft lemon for paper notes. The student's screens add the sky, stickers, glass
 * folders, notebook paper and handwriting.
 * Plain values only, so the web (CSS variables) and the app (StyleSheet) read the same numbers.
 */

export const colors = {
  /**
   * «Форми 2» (29.10 — the app takes the site's look): the ink a near-black violet (his «тёмно-тёмно-
   * фиолетовый», no black, no green), the softer inks the same ink with transparency (never grey). The
   * site draws from `forms2.ts`; these names are the app's.
   */
  ink: '#1F1A35',
  inkSoft: 'rgba(31,26,53,0.78)',
  muted: 'rgba(31,26,53,0.64)',
  faint: 'rgba(31,26,53,0.42)',
  line: '#E4E0D7',
  fill: '#EFECE6',
  /** the page: the warm paper; white blocks on it with no shadow */
  canvas: '#F5F3EF',
  surface: '#FFFFFF',

  /**
   * The dark of the main button and of what is chosen; `accentSoft` — what is on (the paper a shade
   * darker); `lavender` — the fill of a card that matters (the average, a thematic grade).
   */
  accent: '#241A45',
  accentDeep: '#241A45',
  accentSoft: '#E9E5DC',
  lavender: '#DAC8FA',
  /** paper notes («Не забудь») */
  lemon: '#FFE596',

  brand: '#241A45',
  brandDeep: '#241A45',
  brandSoft: '#E9E5DC',
  navy: '#241A45',

  /** the student's sky of the earlier design: now the paper */
  skyTop: '#F5F3EF',
  skyMid: '#F5F3EF',
  skyLow: '#F5F3EF',

  /** notebook paper of the earlier design: now plain white blocks */
  paper: '#FFFFFF',
  paperLine: '#EFECE6',
  paperGrid: '#F2EFEA',
  paperMargin: '#E4E0D7',

  /** a glass folder: its back and the frosted front */
  folderBack: '#F6F6F8',
  folderGlass: 'rgba(255, 255, 255, 0.55)',

  success: '#1C774B',
  successSoft: '#E8F8F0',
  warning: '#97520E',
  warningSoft: '#FFF2E3',
  danger: '#B4431C',
  /** the orange of a counter, the «now» line */
  coral: '#FF6B3D',
  dangerSoft: '#FFECE4',
  live: '#6544C4',
  liveSoft: '#F4ECFF',
  shelter: '#82620A',
  shelterSoft: '#FFF9DE',
} as const;

/**
 * A subject's colour family. A subject shows its colour in its sticker and in small things (a
 * sheet in its folder, a dot); cards stay white.
 */
export interface Swatch {
  /** a flat pastel for a small thing: a sheet, a tab */
  face: string;
  /** a deeper shade: a bar */
  back: string;
  /** a light tint */
  soft: string;
  /** text on `face` and `soft` */
  ink: string;
  /** a small mark on white or paper */
  deep: string;
}

const INK = '#17171B';

export const swatches = {
  sun: { face: '#FFE3A3', back: '#F5C842', soft: '#FFF6DC', ink: INK, deep: '#C28A00' },
  tangerine: { face: '#FFD3C0', back: '#FF9E73', soft: '#FFF0E7', ink: INK, deep: '#E0631A' },
  coral: { face: '#FFCCC7', back: '#FF8F86', soft: '#FFF0EE', ink: INK, deep: '#DC3F3A' },
  pink: { face: '#FFCCE3', back: '#FF94C4', soft: '#FFF0F7', ink: INK, deep: '#D9478A' },
  lilac: { face: '#D3C8FF', back: '#A895FF', soft: '#F2EEFF', ink: INK, deep: '#6A50E6' },
  sky: { face: '#C9E3FF', back: '#7DBDFF', soft: '#EEF6FF', ink: INK, deep: '#1C84D9' },
  teal: { face: '#C2EEE8', back: '#6FD6CB', soft: '#EBFAF8', ink: INK, deep: '#0F938B' },
  mint: { face: '#C8EFDD', back: '#7BD9AE', soft: '#EDFAF3', ink: INK, deep: '#139C62' },
  lime: { face: '#E3F4B2', back: '#C3E26A', soft: '#F6FBE6', ink: INK, deep: '#6B9400' },
} as const satisfies Record<string, Swatch>;

export type SwatchName = keyof typeof swatches;

/**
 * Design v4 tones, taken colour by colour from the reference «weihu» (26.10, «the standard»): each has
 * a `soft` ground for a pill or an icon tile, a card gradient (`from` at the top, more saturated, into
 * a lighter `to`), `fill` for a flat block of the younger classes, `ink` for text on all of them and
 * `icon` for dots, checks and line icons. One family per meaning: the same colour on every screen.
 * `vivid` (27.10, ref. the chips «March 09 — 16», «Weekly», «Zoom»): the bright text and filled icon
 * of a chip on its `soft` ground — deeper than the reference, so it reads (contrast ≥ 3.5 on `soft`).
 */
export interface Tone {
  soft: string;
  from: string;
  to: string;
  fill: string;
  ink: string;
  icon: string;
  vivid: string;
}

export const tones = {
  blue: {
    soft: '#E9EFFD',
    from: '#C9DAFE',
    to: '#E3EAFB',
    fill: '#D6E2FD',
    ink: '#3D5A8F',
    icon: '#3470C9',
    vivid: '#1B70D4',
  },
  violet: {
    soft: '#EEEBFD',
    from: '#D0C9FD',
    to: '#E6E2F8',
    fill: '#DCD6FC',
    ink: '#4F3A9E',
    icon: '#5B3FC0',
    vivid: '#6644E0',
  },
  pink: {
    soft: '#FBE9F7',
    from: '#FFDEFE',
    to: '#F9E1F1',
    fill: '#FCE0F7',
    ink: '#96508F',
    icon: '#C75DB5',
    vivid: '#BE4394',
  },
  red: {
    soft: '#FCE8E7',
    from: '#FACCCE',
    to: '#F8E2DE',
    fill: '#F9D6D6',
    ink: '#A4525A',
    icon: '#E5616A',
    vivid: '#D2433F',
  },
  orange: {
    soft: '#FEF0E2',
    from: '#FAE3CD',
    to: '#FCEFDE',
    fill: '#FBE7D3',
    ink: '#92602F',
    icon: '#D86F07',
    vivid: '#CC6416',
  },
  yellow: {
    soft: '#FDF3E7',
    from: '#FFEDE1',
    to: '#F7EFE4',
    fill: '#FCEEDF',
    ink: '#8E6740',
    icon: '#D9944A',
    vivid: '#AD780A',
  },
  green: {
    soft: '#E3F8EF',
    from: '#C0F4E7',
    to: '#EEFAF3',
    fill: '#CFF5E8',
    ink: '#2B6A4E',
    icon: '#209E4C',
    vivid: '#1C8F4E',
  },
  teal: {
    soft: '#E2F5FB',
    from: '#C8F0FC',
    to: '#E0F3F9',
    fill: '#D2F1FA',
    ink: '#2D7487',
    icon: '#28A6CE',
    vivid: '#1383B0',
  },
  grey: {
    soft: '#F2F2F4',
    from: '#ECECEF',
    to: '#F5F5F7',
    fill: '#E9E9EC',
    ink: '#4D4D56',
    icon: '#8A8A94',
    vivid: '#6E6E78',
  },
} as const satisfies Record<string, Tone>;

export type ToneName = keyof typeof tones;

/**
 * The grid of v4 (research 26.10: 12 columns are the dashboard default, an 8 px step, «inside ≤
 * outside»). Inside the white panel one margin, one gutter and one block padding, so outer edges and
 * text lines meet: the bar, the banners and the blocks start at `margin`, text inside them at
 * `margin + padding`. Phones (compact) use the smaller set. See docs/design-v4.md.
 */
export const grid = {
  columns: 12,
  /** px, multiples of 8 (4 only for small parts and text) */
  step: 8,
  regular: { margin: 24, gutter: 24, padding: 24 },
  compact: { margin: 16, gutter: 16, padding: 16 },
  /** the panel off the screen's edges, the bar's height, the gap from the bar to the page */
  panelInset: 12,
  bar: 56,
  barToPage: 32,
  /** heights of things in rows */
  control: { sm: 32, md: 40 },
  row: { compact: 48, regular: 56, tall: 64 },
  icon: { tile: 40, avatar: 56 },
  radius: { panel: 28, hero: 28, block: 28, blockJunior: 32, row: 20, card: 20, control: 999 },
} as const;

/** The surfaces of v4: the page under the menu (neutral grey), the white panel, its blocks' outline. */
export const surfaces = {
  app: '#F2F2F4',
  panel: '#FFFFFF',
  stroke: '#EAEAEE',
  strokeStrong: '#DCDCE2',
  /** a quiet grey for rare wells and fields (ref. «Horace Mann» #F7F7F7) */
  soft: '#F6F6F7',
  /** the main button: a deep navy, never black (27.10) */
  primary: '#1E2A5E',
} as const;

/** Grade stickers by level (see gradeLevel in @shkola/api): low, middle, good, high. */
export const gradeTones = [
  { bg: '#FFE5E4', fg: '#D43B38' },
  { bg: '#FFF1D6', fg: '#C27A00' },
  { bg: '#E3F0FF', fg: '#2468CF' },
  { bg: '#DDF7EB', fg: '#178A57' },
] as const;

export const radius = { xs: 8, sm: 12, md: 16, lg: 22, xl: 28, xxl: 36, pill: 999 } as const;

export const space = {
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  /** the side margin of a screen */
  gutter: 20,
  xl: 24,
  xxl: 32,
  xxxl: 48,
} as const;

/** Sizes and line heights; the families differ per platform (see each app). */
export const type = {
  display: { fontSize: 40, lineHeight: 44, letterSpacing: -1 },
  title1: { fontSize: 32, lineHeight: 36, letterSpacing: -0.6 },
  title2: { fontSize: 24, lineHeight: 29, letterSpacing: -0.3 },
  title3: { fontSize: 19, lineHeight: 24, letterSpacing: -0.2 },
  headline: { fontSize: 17, lineHeight: 22, letterSpacing: 0 },
  body: { fontSize: 16, lineHeight: 22, letterSpacing: 0 },
  callout: { fontSize: 15, lineHeight: 20, letterSpacing: 0 },
  subhead: { fontSize: 14, lineHeight: 18, letterSpacing: 0 },
  caption: { fontSize: 12, lineHeight: 16, letterSpacing: 0.2 },
  /** Caveat runs small: a size and a half of the text around it */
  hand: { fontSize: 24, lineHeight: 26, letterSpacing: 0 },
  handSmall: { fontSize: 19, lineHeight: 21, letterSpacing: 0 },
} as const;

/**
 * Cards are flat and sit close together; stickers have no shadow. Only a few lifted things (a
 * sheet over the page, a note pinned to paper) cast a short, soft one.
 */
export const shadows = {
  card: '0px 0px 0px rgba(0, 0, 0, 0)',
  lifted: '0px 14px 34px rgba(23, 23, 27, 0.14)',
  sticker: '0px 0px 0px rgba(0, 0, 0, 0)',
  pressed: '0px 2px 8px rgba(23, 23, 27, 0.1)',
} as const;

export const motion = {
  duration: { tap: 120, fast: 180, base: 280, slow: 450, float: 3200, drift: 36000 },
  /** cubic-bezier control points */
  easing: {
    out: [0.16, 1, 0.3, 1],
    inOut: [0.65, 0, 0.35, 1],
    standard: [0.2, 0, 0, 1],
  },
  /** spring settings (damping, stiffness, mass) */
  spring: {
    /** «в'язкий»: heavy and soft with a little overshoot — stacks, folders, sheets */
    viscous: { damping: 24, stiffness: 170, mass: 1.15 },
    /** quick feedback under the finger */
    snappy: { damping: 22, stiffness: 320, mass: 0.9 },
    /** a playful pop for stickers */
    bouncy: { damping: 11, stiffness: 190, mass: 0.9 },
  },
} as const;

/** The vertical rhythm of notebook paper: the ruler of «лінійка» and the cell of «клітинка». */
export const paper = { rule: 28, cell: 22, margin: 56 } as const;
